import { type ChangeEvent } from "react";
import {
  Text,
  Pagination,
  Loader,
  Box,
  Select,
} from "@cruk/cruk-react-components";
import { useRouter } from "next/compat/router";

import { useTracking } from "@fwa/src/hooks/useTracking";
import { useSwrGet } from "@fwa/src/hooks/useSwrGet";
import { fwsUrSuperTeamPageFundraisingPages } from "@fwa/src/services/superTeamService";
import { queryAsString } from "@fwa/src/utils/urlUtils";

import { MemberFundraisingPageDonate } from "@fwa/src/components/MemberFundraisingPageDonate";

import sharedStyles from "@fwa/src/components/styles.module.css";

import type {
  FundraisingPageType,
  SuperTeamMemberSortType,
  FwsResponseData,
} from "@fwa/src/types";

export const SuperTeamFundraisingPages = ({
  teamId,
  itemsPerPage,
}: {
  teamId: string;
  itemsPerPage: number;
}) => {
  const router = useRouter();
  const { trackError } = useTracking();
  const { fundraising_pages_page: fundraisingPagesPage, sort: sortQuery } =
    router?.query || {};
  const fundraisingPagesPageNumber = queryAsString(fundraisingPagesPage);
  const sort = queryAsString(sortQuery || "amount") as SuperTeamMemberSortType;
  const currentPage = parseInt(fundraisingPagesPageNumber || "1", 10);
  const firstItemNumber = Math.max(currentPage - 1, 0) * itemsPerPage + 1;
  const url = teamId
    ? fwsUrSuperTeamPageFundraisingPages({
        teamId,
        currentPage,
        itemsPerPage,
        sort,
      })
    : null;
  const { data, error, totalItems } =
    useSwrGet<FwsResponseData<FundraisingPageType>>(url);
  const fundraisingPages = data?.results || [];

  // We are effectively storing page number in the query string
  const setPageInQueryString = (pageNumber: number) => {
    // pathname isn't going to cut the mustard because it's on the teams page with a [slug]
    // so we need to make the url look like how it should do which is annoying because this list should be
    // agnostic to the page path however it still needs to know how to amend the query string in the URL
    router
      ?.push(
        {
          pathname: router.pathname,
          query: { ...router.query, fundraising_pages_page: pageNumber },
        },
        undefined,
        { shallow: true },
      )
      .catch((err: Error) => {
        trackError(err, {
          component: "SuperTeamFundraisingPages",
          function: "setPageInQueryString",
        });
      });
  };

  const setSortInQueryString = (sortValue: string) => {
    router
      ?.push(
        {
          pathname: router.pathname,
          query: { ...router.query, sort: sortValue },
        },
        undefined,
        { shallow: true },
      )
      .catch((err: Error) => {
        trackError(err, {
          component: "SuperTeamFundraisingPages",
          function: "setSortInQueryString",
        });
      });
  };

  const handleFilterChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const sortValue = e.target.value as SuperTeamMemberSortType;

    setSortInQueryString(sortValue || null);
  };

  return (
    <>
      {/* Error State */}
      {error ? (
        <Text as="span">Unable to get super team fundraisng pages</Text>
      ) : null}

      {/* Loading State */}
      {!error && !data ? <Loader /> : null}

      {/* Empty State */}
      {!error && data && !fundraisingPages.length ? (
        <Text as="span">
          There are currently no fundraising pages for this super team
        </Text>
      ) : null}

      {/* Data State */}
      {!error && data && fundraisingPages.length ? (
        <>
          <Box>
            <div className={sharedStyles["filter-wrapper"]}>
              <Select
                required
                value={sort}
                label="Sort Giving Pages by"
                onChange={handleFilterChange}
                aria-controls="memberList"
                hideRequiredIndicationInLabel
              >
                <option value="alpha">A-Z</option>
                <option value="amount">amount raised</option>
                <option value="recent">recently joined</option>
              </Select>
            </div>
          </Box>

          <Box>
            <div className={sharedStyles["row-space-around"]}>
              <Text>
                {`Showing ${firstItemNumber} to ${
                  firstItemNumber + fundraisingPages.length - 1
                } of ${totalItems} Giving Pages`}
              </Text>
            </div>
          </Box>
          <Box>
            <ul
              className={sharedStyles["member-list"]}
              id="memberList"
              aria-live="assertive"
            >
              {fundraisingPages.map((fundraisingPage: FundraisingPageType) => (
                <li key={fundraisingPage.uniqueId}>
                  <MemberFundraisingPageDonate
                    fundraisingPage={fundraisingPage}
                  />
                </li>
              ))}
            </ul>
          </Box>
          <Box>
            <Pagination
              aria-controls="memberList"
              current={currentPage}
              perPage={itemsPerPage}
              items={totalItems}
              hideLast={false}
              pagerCallback={(n: number) => {
                setPageInQueryString(n);
              }}
            />
          </Box>
        </>
      ) : null}
    </>
  );
};

export default SuperTeamFundraisingPages;
